import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { Nullable, Coordinate } from "@poscon/shared-types";
import type { RootState, RootThunkAction } from "~redux/store";
import type { Rectangle } from "pixi.js";
import { EdstGeomapConfig } from "@poscon/shared-types/eram";

export type AircraftDisplayOptions = {
  aircraftListFilter: ["Aircraft List Filter", boolean];
  altitudeFilterLimits: ["Altitude Filter Limits", boolean];
  filterAbove: ["Filter Above", Nullable<number>];
  filterBelow: ["Filter Below", Nullable<number>];
  autoDatablockOffset: ["Auto Datablock Offset", boolean];
  mspLabels: ["MSP/MEP Labels", boolean];
  routePreviewMinutes: ["Route Preview (minutes)", number];
};

export type GpdState = {
  geomapConfig: Nullable<EdstGeomapConfig["gpd"]>;
  enabledGeomaps: string[];
  aircraftDisplayOptions: AircraftDisplayOptions;
  centerOverride: Coordinate;
  scale: number;
  suppressed: boolean;
  planData: Record<string, unknown>[];
};

const initialState: GpdState = {
  geomapConfig: null,
  enabledGeomaps: [],
  aircraftDisplayOptions: {
    aircraftListFilter: ["Aircraft List Filter", false],
    altitudeFilterLimits: ["Altitude Filter Limits", false],
    filterAbove: ["Filter Above", null],
    filterBelow: ["Filter Below", null],
    autoDatablockOffset: ["Auto Datablock Offset", false],
    mspLabels: ["MSP/MEP Labels", false],
    routePreviewMinutes: ["Route Preview (minutes)", 0],
  },
  centerOverride: [0, 0],
  scale: 1,
  suppressed: false,
  planData: [],
};

export const gpdSlice = createSlice({
  name: "gpd",
  initialState,
  reducers: {
    setGpdState(state, action: PayloadAction<GpdState>) {
      return action.payload;
    },
    setGpdGeomapConfig(state, action: PayloadAction<Nullable<EdstGeomapConfig["gpd"]>>) {
      state.geomapConfig = action.payload;
      if (state.geomapConfig) {
        state.enabledGeomaps = Object.entries(state.geomapConfig.geomaps).flatMap(
          ([id, { defaultEnabled }]) => (defaultEnabled ? [id] : [])
        );
      } else {
        state.enabledGeomaps = [];
      }
    },
    setGpdGeomapEnabled(state, action: PayloadAction<{ id: string; enabled: boolean }>) {
      const { id, enabled } = action.payload;
      if (enabled) {
        if (!state.enabledGeomaps.includes(id)) {
          state.enabledGeomaps.push(id);
        }
      } else {
        state.enabledGeomaps = state.enabledGeomaps.filter((geomapId) => geomapId !== id);
      }
    },
    addGpdPlanData(state, action: PayloadAction<Record<string, unknown>>) {
      state.planData.push(action.payload);
    },
    removeGpdPlanData(state, action: PayloadAction<number>) {
      if (action.payload < state.planData.length - 1 && action.payload >= 0) {
        state.planData.splice(action.payload);
      }
    },
    setGpdAircraftDisplayOptions(state, action: PayloadAction<AircraftDisplayOptions>) {
      state.aircraftDisplayOptions = action.payload;
    },
    setGpdSuppressed(state, action: PayloadAction<boolean>) {
      state.suppressed = action.payload;
    },
    toggleGpdSuppressed(state) {
      state.suppressed = !state.suppressed;
    },
    setGpdCenterOverride(state, action: PayloadAction<Coordinate>) {
      state.centerOverride = action.payload;
    },
    translateGpdCenter(state, action: PayloadAction<Coordinate>) {
      const [dx, dy] = action.payload;
      state.centerOverride[0] -= state.scale * dx;
      state.centerOverride[1] -= state.scale * dy;
    },
    setGpdScale(state, action: PayloadAction<{ value: number; rect?: Rectangle }>) {
      const { value: newScale, rect } = action.payload;
      if (rect) {
        const oldScale = state.scale;
        state.centerOverride[0] -= rect.width / 2;
        state.centerOverride[1] -= rect.height / 2;
        state.centerOverride[0] *= newScale / oldScale;
        state.centerOverride[1] *= newScale / oldScale;
        state.centerOverride[0] += rect.width / 2;
        state.centerOverride[1] += rect.height / 2;
      }
      state.scale = newScale;
    },
  },
});

export const {
  setGpdCenterOverride,
  addGpdPlanData,
  removeGpdPlanData,
  setGpdAircraftDisplayOptions,
  setGpdSuppressed,
  toggleGpdSuppressed,
  translateGpdCenter,
  setGpdScale,
  setGpdState,
  setGpdGeomapConfig,
  setGpdGeomapEnabled,
} = gpdSlice.actions;
export const reducer = gpdSlice.reducer;

export const gpdGeomapConfigSelector = (state: RootState) => state.gpd.geomapConfig;
export const gpdEnabledGeomapsSelector = (state: RootState) => state.gpd.enabledGeomaps;
export const gpdStateSelector = (state: RootState) => state.gpd;
export const gpdSuppressedSelector = (state: RootState) => state.gpd.suppressed;
export const gpdAircraftDisplayOptionsSelector = (state: RootState) => state.gpd.aircraftDisplayOptions;
export const gpdPlanDataSelector = (state: RootState) => state.gpd.planData;
export const gpdCenterOverrideSelector = (state: RootState) => state.gpd.centerOverride;
export const gpdScaleSelector = (state: RootState) => state.gpd.scale;

export const setGpdScaleToRange = (range: number, prevRange: number, rect: Rectangle): RootThunkAction => {
  return (dispatch, getState) => {
    const scale = gpdScaleSelector(getState());
    dispatch(
      setGpdScale({
        value: (scale * prevRange) / Math.max(range, 1),
        rect,
      })
    );
  };
};
